import React, { useState, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { DailyProvider } from '@daily-co/daily-react';
import Daily from '@daily-co/daily-js';
import { AuthProvider } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { FeatureGateProvider } from './contexts/FeatureGateContext';
import { JournalProvider } from './contexts/JournalContext';
import { VoiceProvider } from './contexts/VoiceContext';
import { CrisisProvider } from './contexts/CrisisContext';
import { AIAnalysisProvider } from './contexts/AIAnalysisContext';
import { DemoModeProvider } from './contexts/DemoModeContext';
import { useEffect, useRef } from 'react';
import LandingPage from './components/LandingPage';
import AuthPage from './components/AuthPage';
import Dashboard from './components/Dashboard';
import FeatureUnlockNotification from './components/FeatureUnlockNotification';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import DemoModeIndicator from './components/DemoModeIndicator';
import BackToDemoButton from './components/BackToDemoButton';
import { usePWA } from './hooks/usePWA';
import AdminNavLink from './components/AdminNavLink';
import { SessionType } from './lib/tavus';

// Lazy load heavy components
const Profile = lazy(() => import('./components/Profile'));
const Journal = lazy(() => import('./components/Journal'));
const JournalEntry = lazy(() => import('./components/JournalEntry'));
const VoiceJournal = lazy(() => import('./components/VoiceJournal'));
const CrisisSupport = lazy(() => import('./components/CrisisSupport'));
const SafetyPlan = lazy(() => import('./components/SafetyPlan'));
const ResourcesDirectory = lazy(() => import('./components/ResourcesDirectory'));
const SubscriptionPlans = lazy(() => import('./components/SubscriptionPlans'));
const ConsentManager = lazy(() => import('./components/ConsentManager'));
const SubscriptionManager = lazy(() => import('./components/SubscriptionManager'));
const AvatarCompanion = lazy(() => import('./components/AvatarCompanion'));
const AvatarSettings = lazy(() => import('./components/AvatarSettings'));
const AudioLibrary = lazy(() => import('./components/AudioLibrary'));
const AIAnalysisDashboard = lazy(() => import('./components/AIAnalysisDashboard'));
const BehavioralDashboard = lazy(() => import('./components/BehavioralDashboard'));
const HowItWorks = lazy(() => import('./components/HowItWorks'));
const Privacy = lazy(() => import('./components/Privacy'));
const Terms = lazy(() => import('./components/Terms'));
const Support = lazy(() => import('./components/Support'));
const AboutUs = lazy(() => import('./components/AboutUs'));
const OnboardingFlow = lazy(() => import('./components/OnboardingFlow'));

// Global Daily call object instance to prevent duplicates
let dailyCallObjectInstance: ReturnType<typeof Daily.createCallObject> | null = null;

function App() {
  // Create a ref to store the global audio context
  const globalAudioContextRef = useRef<AudioContext | null>(null);
  const [callObject, setCallObject] = useState<ReturnType<typeof Daily.createCallObject> | null>(null);
  const [avatarCompanionState, setAvatarCompanionState] = useState<{
    isVisible: boolean;
    sessionType: SessionType;
  }>({
    isVisible: false,
    sessionType: 'check-in'
  });

  usePWA();

  // Initialize Daily call object
  useEffect(() => {
    // Check if we already have a global call object to prevent duplicates
    if (dailyCallObjectInstance) {
      setCallObject(dailyCallObjectInstance);
      return;
    }

    try {
      console.log("Attempting to create Daily call object...");
      // Create a properly configured Daily call object for Tavus integration
      dailyCallObjectInstance = Daily.createCallObject({
        audioSource: true, // Enable audio capture for two-way conversation
        videoSource: false, // We only need to receive video from avatar
        dailyConfig: {
          disableAudioProcessing: false,
          // Use inputSettings instead of userMediaAudioConstraints
          inputSettings: {
            audio: {
              enabled: true, // Enable audio input
              settings: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
              }
            },
            video: {
              settings: {
                width: { ideal: 1280 },
                height: { ideal: 720 }
              }
            }
          },
          // Use sendSettings instead of camSimulcastEncodings
          sendSettings: {
            video: {
              encodings: [
                { maxBitrate: 600000, maxFramerate: 30, scaleResolutionDownBy: 2 },
                { maxBitrate: 1200000, maxFramerate: 30, scaleResolutionDownBy: 1 }
              ]
            }
          }
        }
      });
      console.log("Daily call object created successfully.");
      setCallObject(dailyCallObjectInstance);
    } catch (error) {
      console.error("Error creating Daily call object:", error);
      // Optionally, you could set an error state here to display a message to the user
    }
  }, []);

  // Initialize and resume AudioContext on user interaction
  useEffect(() => {
    // Create the audio context if it doesn't exist
    if (!globalAudioContextRef.current) {
      try {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        globalAudioContextRef.current = new AudioContext();
        console.log("Global AudioContext created:", globalAudioContextRef.current.state);
      } catch (error) {
        console.error("Failed to create global AudioContext:", error);
      }
    }

    // Function to resume the audio context
    const resumeAudioContext = async () => {
      if (globalAudioContextRef.current && globalAudioContextRef.current.state === 'suspended') {
        try {
          await globalAudioContextRef.current.resume();
          console.log("Global AudioContext resumed successfully:", globalAudioContextRef.current.state);
        } catch (error) {
          console.error("Failed to resume AudioContext:", error);
        }
      }
    };

    // Add event listeners for user interactions
    const userInteractionEvents = ['mousedown', 'keydown', 'touchstart', 'click'];
    userInteractionEvents.forEach(event => {
      document.addEventListener(event, resumeAudioContext, { once: false });
    });

    // Try to resume immediately (might work if this effect runs after a user interaction)
    resumeAudioContext();

    // Cleanup event listeners on unmount
    return () => {
      userInteractionEvents.forEach(event => {
        document.removeEventListener(event, resumeAudioContext);
      });
    };
  }, []);

  // Don't render providers until callObject is ready
  if (!callObject) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  return (
    <DailyProvider callObject={callObject}>
      <AuthProvider>
        <SubscriptionProvider>
          <FeatureGateProvider>
            <JournalProvider>
              <VoiceProvider callObject={callObject}>
                <CrisisProvider>
                  <AIAnalysisProvider>
                    <DemoModeProvider>
                      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
                        <AdminNavLink />
                        <Suspense fallback={
                          <div className="min-h-screen flex items-center justify-center">
                            <div className="text-center">
                              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                              <p className="text-gray-600">Loading...</p>
                            </div>
                          </div>
                        }>
                          <Routes>
                            <Route path="/" element={<LandingPage />} />
                            <Route path="/auth" element={<AuthPage />} />
                            <Route path="/onboarding" element={<OnboardingFlow />} />
                          <Route path="/dashboard" element={<Dashboard setAvatarCompanionState={setAvatarCompanionState} />} />
                          <Route path="/profile/*" element={<Profile />} />
                          <Route path="/consent" element={<ConsentManager />} />
                          <Route path="/subscription/manage" element={<SubscriptionManager />} />
                          <Route path="/subscription/plans" element={<SubscriptionPlans />} />
                          <Route path="/journal" element={<Journal />} />
                          <Route path="/journal/new" element={<JournalEntry />} />
                          <Route path="/journal/:id" element={<JournalEntry />} />
                          <Route path="/journal/voice" element={<VoiceJournal />} />
                          <Route path="/voice-journal" element={<VoiceJournal />} />
                          <Route path="/crisis-support" element={<CrisisSupport />} />
                          <Route path="/crisis" element={<CrisisSupport />} />
                          <Route path="/safety-plan" element={<SafetyPlan />} />
                          <Route path="/resources" element={<ResourcesDirectory />} />
                          <Route path="/avatar" element={<AvatarCompanion />} />
                          <Route path="/avatar/settings" element={<AvatarSettings />} />
                          <Route path="/audio" element={<AudioLibrary />} />
                          <Route path="/analysis" element={<AIAnalysisDashboard />} />
                          <Route path="/behavioral" element={<BehavioralDashboard />} />
                          <Route path="/how-it-works" element={<HowItWorks />} />
                          <Route path="/privacy" element={<Privacy />} />
                          <Route path="/terms" element={<Terms />} />
                          <Route path="/support" element={<Support />} />
                          <Route path="/about-us" element={<AboutUs />} />
                          </Routes>
                        </Suspense>

                        <FeatureUnlockNotification />
                        <PWAInstallPrompt />
                        <OfflineIndicator />
                        <DemoModeIndicator />
                        <BackToDemoButton />
                        
                        {/* Avatar Companion - Centrally managed */}
                        {avatarCompanionState.isVisible && (
                          <AvatarCompanion
                            sessionType={avatarCompanionState.sessionType}
                            onClose={() => setAvatarCompanionState(prev => ({ ...prev, isVisible: false }))}
                          />
                        )}
                      </div>
                    </DemoModeProvider>
                  </AIAnalysisProvider>
                </CrisisProvider>
              </VoiceProvider>
            </JournalProvider>
          </FeatureGateProvider>
        </SubscriptionProvider>
      </AuthProvider>
    </DailyProvider>
  );
}

export default App;